import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { useStatsig } from '../hooks/useStatsig';
import { usePlayerSavedStatusBatch } from './usePlayerSavedStatusBatch';

const EMPTY_ROW_STATUS = { isSaved: false, savedListIds: [], isLoading: false };
const noop = () => {};

// A page-scoped shared source of saved-player-status. Mounted once around every bookmark surface on the
// profile page (header + Verified Rankings leaderboard), it owns a single batched saved-status map so a
// save/remove on one surface re-renders every co-mounted bookmark for that player — no per-surface copies
// to drift out of sync. The default value keeps a consumer rendered outside a provider safe (not-saved,
// no fetch) rather than throwing.
const SavedPlayerStatusContext = createContext({
    gateEnabled: false,
    fetchEnabled: false,
    getRowStatus: () => EMPTY_ROW_STATUS,
    handleSaveSuccess: noop,
    handleRemoveSuccess: noop,
    registerPlayerIds: noop,
});

export const useSavedPlayerStatus = () => useContext(SavedPlayerStatusContext);

let bucketSequence = 0;

// Contributes a consumer's player ids to the shared batch and keeps them registered for the life of the
// mount. Ids are withheld until the provider is fetch-enabled (Power viewer + gate on) so free / gate-off
// surfaces never probe their mock ids.
export const useRegisterSavedPlayerIds = (playerIds) => {
    const { registerPlayerIds, fetchEnabled } = useSavedPlayerStatus();
    const bucketKeyRef = useRef(null);
    if (bucketKeyRef.current === null) {
        bucketSequence += 1;
        bucketKeyRef.current = `bucket-${bucketSequence}`;
    }

    const contributedIds = fetchEnabled ? (playerIds || []) : [];
    const idsSignature = contributedIds.join(',');

    useEffect(() => {
        const bucketKey = bucketKeyRef.current;
        registerPlayerIds(bucketKey, contributedIds);
        return () => registerPlayerIds(bucketKey, []);
        // idsSignature captures the id-set change; contributedIds identity churns every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsSignature, registerPlayerIds]);
};

export const SavedPlayerStatusProvider = ({ children }) => {
    const { checkGate, featureGates } = useStatsig();
    const isPowerUser = useSelector((state) => !!state.auth?.user?.isPowered);
    const gateEnabled = checkGate(featureGates.SAVED_PLAYER_LIST_ROLLOUT);
    const fetchEnabled = gateEnabled && isPowerUser;

    const [idBuckets, setIdBuckets] = useState({});

    const registerPlayerIds = useCallback((bucketKey, ids) => {
        setIdBuckets((prev) => {
            const nextSignature = (ids || []).join(',');
            const prevSignature = prev[bucketKey] ? prev[bucketKey].join(',') : undefined;
            if (nextSignature === prevSignature) {
                return prev;
            }
            if (!ids || ids.length === 0) {
                if (prev[bucketKey] === undefined) {
                    return prev;
                }
                const next = { ...prev };
                delete next[bucketKey];
                return next;
            }
            return { ...prev, [bucketKey]: ids };
        });
    }, []);

    const aggregatedIds = useMemo(() => {
        const seen = new Set();
        const ids = [];
        Object.keys(idBuckets).forEach((bucketKey) => {
            idBuckets[bucketKey].forEach((id) => {
                if (id != null && !seen.has(id)) {
                    seen.add(id);
                    ids.push(id);
                }
            });
        });
        return ids;
    }, [idBuckets]);

    const { getRowStatus, handleSaveSuccess, handleRemoveSuccess } = usePlayerSavedStatusBatch(
        aggregatedIds,
        { enabled: fetchEnabled },
    );

    const value = useMemo(() => ({
        gateEnabled,
        fetchEnabled,
        getRowStatus,
        handleSaveSuccess,
        handleRemoveSuccess,
        registerPlayerIds,
    }), [gateEnabled, fetchEnabled, getRowStatus, handleSaveSuccess, handleRemoveSuccess, registerPlayerIds]);

    return (
        <SavedPlayerStatusContext.Provider value={value}>
            {children}
        </SavedPlayerStatusContext.Provider>
    );
};

SavedPlayerStatusProvider.propTypes = {
    children: PropTypes.node,
};

SavedPlayerStatusProvider.defaultProps = {
    children: null,
};
